import React from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';

const STEP_EXTENSIONS = ['.stp', '.step'];
const DRAWING_EXTENSIONS = ['.dxf', '.dwg']; // dwg는 올릴 수는 있지만 서버가 경고로 안내한다.

const hasExtension = (fileName, extensions) => {
  const lower = (fileName || '').toLowerCase();
  return extensions.some((ext) => lower.endsWith(ext));
};

/**
 * 1단계: 3D 설계 파일(STEP) + 선택적 도면(DXF/DWG)을 올린다.
 * 두 개의 드롭존을 따로 둔 이유: 하나는 필수(STEP), 하나는 선택(도면)이라 섞이면
 * "뭘 어디 놓아야 하는지" 헷갈린다.
 */
const CadQuoteUploadStep = ({
  stepFile,
  drawingFile,
  onStepFileChange,
  onDrawingFileChange,
  onAnalyze,
  loading,
  progress,
  errorMessage,
}) => {
  const stepDropzone = useDropzone({
    multiple: false,
    disabled: loading,
    onDrop: (accepted) => {
      const file = accepted[0];
      if (file && hasExtension(file.name, STEP_EXTENSIONS)) {
        onStepFileChange(file);
      }
    },
  });

  const drawingDropzone = useDropzone({
    multiple: false,
    disabled: loading,
    onDrop: (accepted) => {
      const file = accepted[0];
      if (file && hasExtension(file.name, DRAWING_EXTENSIONS)) {
        onDrawingFileChange(file);
      }
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom>1. 도면 올리기</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        3D 설계 파일(.stp/.step)을 올려주세요. 조립품 stp도 부품별로 나눠 계산합니다.
        표제란(재질/수량/품번)을 같이 읽고 싶으면 도면(.dxf)도 같이 올릴 수 있습니다 - 선택사항입니다.
      </Typography>

      <Stack spacing={2}>
        <Box
          {...stepDropzone.getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: stepFile ? 'success.main' : 'grey.400',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            cursor: loading ? 'default' : 'pointer',
            bgcolor: stepDropzone.isDragActive ? 'action.hover' : 'background.paper',
          }}
        >
          <input {...stepDropzone.getInputProps()} />
          <UploadFileIcon fontSize="large" color={stepFile ? 'success' : 'action'} />
          {stepFile ? (
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body2">{stepFile.name}</Typography>
              <Chip
                size="small"
                icon={<CloseIcon />}
                label="제거"
                onClick={(e) => {
                  e.stopPropagation();
                  onStepFileChange(null);
                }}
              />
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ mt: 1 }}>
              여기로 STEP 파일을 드래그하거나 눌러서 선택하세요 (.stp, .step)
            </Typography>
          )}
        </Box>

        <Box
          {...drawingDropzone.getRootProps()}
          sx={{
            border: '1px dashed',
            borderColor: drawingFile ? 'info.main' : 'grey.300',
            borderRadius: 1,
            p: 2,
            textAlign: 'center',
            cursor: loading ? 'default' : 'pointer',
            bgcolor: drawingDropzone.isDragActive ? 'action.hover' : 'background.paper',
          }}
        >
          <input {...drawingDropzone.getInputProps()} />
          <DescriptionIcon color={drawingFile ? 'info' : 'action'} />
          {drawingFile ? (
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body2">{drawingFile.name}</Typography>
              <Chip
                size="small"
                icon={<CloseIcon />}
                label="제거"
                onClick={(e) => {
                  e.stopPropagation();
                  onDrawingFileChange(null);
                }}
              />
            </Stack>
          ) : (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              (선택) 도면 파일을 드래그하거나 눌러서 선택하세요 (.dxf) - 재질/수량/품번 자동 인식용
            </Typography>
          )}
        </Box>

        {loading && (
          <Box>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">업로드 중... {progress}%</Typography>
          </Box>
        )}

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <Box sx={{ textAlign: 'right' }}>
          <Button
            variant="contained"
            disabled={!stepFile || loading}
            onClick={onAnalyze}
          >
            분석하기
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default CadQuoteUploadStep;
